import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
  LinearProgress,
  InputAdornment,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  useMediaQuery,
  Fade,
  Skeleton,
  Snackbar,
  Alert,
  Badge,
  Stack,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  TrendingDown as LowIcon,
  Restaurant as RecipeIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  CheckCircle as CheckIcon,
  InfoOutlined as InfoIcon,      // ✅ FIXED: Use InfoOutlined instead of ErrorOutline
  // OR use Warning as ErrorIcon
} from '@mui/icons-material';

const UNITS = ['pcs', 'kg', 'gram', 'liter', 'ml', 'dozen', 'pack', 'box', 'bottle', 'can', 'piece', 'bag'];
const CATEGORIES = ['general', 'meat', 'vegetables', 'dairy', 'spices', 'beverages', 'packaging', 'frozen', 'bakery', 'dry goods'];

const Inventory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [itemDialog, setItemDialog] = useState(false);
  const [itemDialogMode, setItemDialogMode] = useState('add');
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '',
    unit: 'kg',
    current_stock: 0,
    min_stock: 10,
    max_stock: 100,
    reorder_point: 20,
    cost_per_unit: 0,
    supplier: '',
    category: 'general',
  });

  const [stockDialog, setStockDialog] = useState(false);
  const [stockAction, setStockAction] = useState('in');
  const [stockItem, setStockItem] = useState(null);
  const [stockQuantity, setStockQuantity] = useState('');
  const [stockNote, setStockNote] = useState('');

  const [recipeDialog, setRecipeDialog] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  const [recipeIngredients, setRecipeIngredients] = useState([]);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ==================== HELPERS ====================
  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const formatPrice = useCallback((amount) => {
    const val = Number(amount) || 0;
    return `PKR ${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const formatNumber = useCallback((num) => {
    return Number(num || 0).toFixed(1);
  }, []);

  // ==================== SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return window.electronAPI;
    }
    return null;
  }, []);

  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Inventory] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== LOAD DATA ====================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = getAPI();
      let inv, recs, menu;

      if (!api) {
        // ✅ BROWSER MODE
        console.log('[Inventory] Browser mode - using storage');
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');

        [inv, recs, menu] = await Promise.all([
          storage.getInventory ? storage.getInventory() : Promise.resolve([]),
          storage.getRecipes ? storage.getRecipes() : Promise.resolve([]),
          storage.getMenuItems ? storage.getMenuItems() : Promise.resolve([]),
        ]);
      } else {
        // ✅ ELECTRON MODE
        console.log('[Inventory] Electron mode - using electronAPI');
        [inv, recs, menu] = await Promise.all([
          api.getInventory(),
          api.getRecipes(),
          api.getMenuItems(),
        ]);
      }

      setInventory(inv || []);
      setRecipes(recs || []);
      setMenuItems(menu || []);
    } catch (err) {
      console.error('[Inventory] Load error:', err);
      showMessage('❌ Data load nahi ho saki!', 'error');
    } finally {
      setLoading(false);
    }
  }, [getAPI, getStorage, showMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==================== FILTERS ====================
  const filteredInventory = inventory.filter((item) => {
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.supplier?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'low' && Number(item.current_stock) <= Number(item.reorder_point)) ||
      (filterStatus === 'critical' && Number(item.current_stock) <= Number(item.min_stock)) ||
      (filterStatus === 'ok' && Number(item.current_stock) > Number(item.reorder_point));
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const lowStockItems = inventory.filter((item) => 
    Number(item.current_stock) <= Number(item.reorder_point)
  );
  
  const criticalItems = inventory.filter((item) => 
    Number(item.current_stock) <= Number(item.min_stock)
  );

  const totalStockValue = inventory.reduce((sum, i) => 
    sum + (Number(i.current_stock) * Number(i.cost_per_unit)), 0
  );

  // ==================== STATUS HELPERS ====================
  const getStockStatus = useCallback((item) => {
    const current = Number(item.current_stock) || 0;
    const min = Number(item.min_stock) || 0;
    const reorder = Number(item.reorder_point) || 0;
    const max = Number(item.max_stock) || 100;

    if (current <= min) return { label: 'Critical', color: '#d32f2f', bg: '#ffebee', icon:<WarningIcon fontSize="small" />};
    if (current <= reorder) return { label: 'Low', color: '#ff9800', bg: '#fff3e0', icon: <WarningIcon fontSize="small" /> };
    if (current >= max) return { label: 'Full', color: '#4caf50', bg: '#e8f5e9', icon: <CheckIcon fontSize="small" /> };
    return { label: 'OK', color: '#2196f3', bg: '#e3f2fd', icon: <InfoIcon fontSize="small" /> };
  }, []);

  const getStockPercentage = useCallback((item) => {
    const current = Number(item.current_stock) || 0;
    const max = Number(item.max_stock) || 100;
    return Math.min(100, Math.max(0, (current / max) * 100));
  }, []);

  // ==================== CRUD OPERATIONS ====================
  const openAddItem = () => {
    setItemDialogMode('add');
    setItemForm({
      name: '',
      unit: 'kg',
      current_stock: 0,
      min_stock: 10,
      max_stock: 100,
      reorder_point: 20,
      cost_per_unit: 0,
      supplier: '',
      category: 'general',
    });
    setEditingItem(null);
    setItemDialog(true);
  };

  const openEditItem = (item) => {
    setItemDialogMode('edit');
    setItemForm({ ...item });
    setEditingItem(item);
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.name.trim()) {
      showMessage('❌ Item name required!', 'error');
      return;
    }
    try {
      const api = getAPI();
      const payload = {
        ...itemForm,
        current_stock: parseFloat(itemForm.current_stock) || 0,
        min_stock: parseFloat(itemForm.min_stock) || 0,
        max_stock: parseFloat(itemForm.max_stock) || 100,
        reorder_point: parseFloat(itemForm.reorder_point) || 0,
        cost_per_unit: parseFloat(itemForm.cost_per_unit) || 0,
      };

      if (!api) {
        // ✅ BROWSER MODE
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        
        if (itemDialogMode === 'add') {
          await storage.addInventoryItem(payload);
        } else {
          await storage.deleteInventoryItem(editingItem.id);
          await storage.addInventoryItem(payload);
        }
      } else {
        // ✅ ELECTRON MODE
        if (itemDialogMode === 'add') {
          await api.addInventoryItem(payload);
        } else {
          // Update via delete + add
          await api.deleteInventoryItem?.(editingItem.id);
          await api.addInventoryItem(payload);
        }
      }
      
      setItemDialog(false);
      showMessage(`✅ ${itemDialogMode === 'add' ? 'Added' : 'Updated'} ${itemForm.name}!`, 'success');
      loadData();
    } catch (err) {
      console.error('[Inventory] Save error:', err);
      showMessage(`❌ Save nahi ho saka: ${err.message}`, 'error');
    }
  };

  const openStockDialog = (item, action) => {
    setStockItem(item);
    setStockAction(action);
    setStockQuantity('');
    setStockNote('');
    setStockDialog(true);
  };

  const handleStockUpdate = async () => {
    if (!stockItem || !stockQuantity) {
      showMessage('❌ Please enter quantity!', 'error');
      return;
    }
    try {
      const api = getAPI();
      const qty = stockAction === 'in' ? Math.abs(parseFloat(stockQuantity)) : -Math.abs(parseFloat(stockQuantity));

      if (!api) {
        // ✅ BROWSER MODE
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        await storage.updateStock(stockItem.id, qty);
      } else {
        // ✅ ELECTRON MODE
        await api.updateStock(stockItem.id, qty);
      }
      
      setStockDialog(false);
      showMessage(`✅ Stock ${stockAction === 'in' ? 'received' : 'used'} ${Math.abs(qty)} ${stockItem.unit}`, 'success');
      loadData();
    } catch (err) {
      console.error('[Inventory] Stock update error:', err);
      showMessage(`❌ Stock update nahi ho saka: ${err.message}`, 'error');
    }
  };

  const openRecipeDialog = () => {
    setSelectedMenuItem('');
    setRecipeIngredients([]);
    setRecipeDialog(true);
  };

  const addRecipeIngredient = () => {
    setRecipeIngredients([...recipeIngredients, { inventory_item_id: '', quantity_needed: '', unit: 'kg' }]);
  };

  const updateRecipeIngredient = (index, field, value) => {
    const updated = [...recipeIngredients];
    updated[index][field] = value;
    setRecipeIngredients(updated);
  };

  const removeRecipeIngredient = (index) => {
    setRecipeIngredients(recipeIngredients.filter((_, i) => i !== index));
  };

  const saveRecipe = async () => {
    if (!selectedMenuItem || recipeIngredients.length === 0) {
      showMessage('❌ Select menu item and add ingredients!', 'error');
      return;
    }
    try {
      const api = getAPI();
      
      if (!api) {
        // ✅ BROWSER MODE
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        for (const ing of recipeIngredients) {
          if (ing.inventory_item_id && ing.quantity_needed) {
            await storage.addRecipe({
              menu_item_id: parseInt(selectedMenuItem),
              inventory_item_id: parseInt(ing.inventory_item_id),
              quantity_needed: parseFloat(ing.quantity_needed),
              unit: ing.unit,
            });
          }
        }
      } else {
        // ✅ ELECTRON MODE
        for (const ing of recipeIngredients) {
          if (ing.inventory_item_id && ing.quantity_needed) {
            await api.addRecipe({
              menu_item_id: parseInt(selectedMenuItem),
              inventory_item_id: parseInt(ing.inventory_item_id),
              quantity_needed: parseFloat(ing.quantity_needed),
              unit: ing.unit,
            });
          }
        }
      }
      
      setRecipeDialog(false);
      showMessage('✅ Recipe added successfully!', 'success');
      loadData();
    } catch (err) {
      console.error('[Inventory] Recipe save error:', err);
      showMessage(`❌ Recipe save nahi ho saki: ${err.message}`, 'error');
    }
  };

  const confirmDelete = (item) => {
    setDeleteConfirm({ open: true, id: item.id, name: item.name });
  };

  const handleDelete = async () => {
    try {
      const api = getAPI();
      
      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        await storage.deleteInventoryItem(deleteConfirm.id);
      } else {
        await api.deleteInventoryItem(deleteConfirm.id);
      }
      
      setDeleteConfirm({ ...deleteConfirm, open: false });
      showMessage(`✅ ${deleteConfirm.name} deleted!`, 'success');
      loadData();
    } catch (err) {
      console.error('[Inventory] Delete error:', err);
      showMessage(`❌ Delete nahi ho saka: ${err.message}`, 'error');
    }
  };

  // ==================== SKELETON ====================
  const TableSkeleton = () => (
    <TableRow>
      <TableCell colSpan={7}>
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" width="100%" height={40} />
          <Skeleton variant="text" width="100%" height={40} />
          <Skeleton variant="text" width="100%" height={40} />
        </Box>
      </TableCell>
    </TableRow>
  );

  // ==================== RENDER INVENTORY TAB ====================
  const renderInventoryTab = () => (
    <Box>
      {/* Filters */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 2, 
        borderRadius: 3, 
        display: 'flex', 
        gap: { xs: 1, sm: 2 }, 
        flexWrap: 'wrap', 
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
      }}>
        <TextField
          size="small"
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: { xs: '100%', sm: 200 } }}
        />
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={filterCategory}
            exclusive
            onChange={(e, val) => val && setFilterCategory(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.3, sm: 0.5 },
              }
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            {CATEGORIES.slice(0, isMobile ? 4 : 8).map((cat) => (
              <ToggleButton key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>{cat}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={(e, val) => val && setFilterStatus(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.3, sm: 0.5 },
              }
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="critical" sx={{ color: '#d32f2f' }}>Critical</ToggleButton>
            <ToggleButton value="low" sx={{ color: '#ff9800' }}>Low</ToggleButton>
            <ToggleButton value="ok" sx={{ color: '#4caf50' }}>OK</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddItem}
          fullWidth={isMobile}
          sx={{ 
            bgcolor: '#ff6b35', 
            '&:hover': { bgcolor: '#e55a2b' }, 
            borderRadius: 2,
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {isMobile ? 'Add' : 'Add Item'}
        </Button>
      </Paper>

      {/* Alerts */}
      {criticalItems.length > 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, bgcolor: '#ffebee', border: '2px solid #d32f2f' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d32f2f', mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
           <WarningIcon fontSize="small" />CRITICAL STOCK ALERT ({criticalItems.length} items)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {criticalItems.map((item) => (
              <Chip
                key={item.id}
                size="small"
                icon={<LowIcon fontSize="small" />}
                label={`${item.name}: ${formatNumber(item.current_stock)} ${item.unit}`}
                sx={{ bgcolor: '#fff', color: '#d32f2f', fontWeight: 700 }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {lowStockItems.length > 0 && criticalItems.length === 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ed6c02', mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
            <WarningIcon fontSize="small" /> Low Stock Alert ({lowStockItems.length} items)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {lowStockItems.map((item) => (
              <Chip
                key={item.id}
                size="small"
                icon={<LowIcon fontSize="small" />}
                label={`${item.name}: ${formatNumber(item.current_stock)} ${item.unit}`}
                sx={{ bgcolor: '#fff', color: '#ed6c02', fontWeight: 600 }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Item</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Category</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Stock</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' }, display: { xs: 'none', md: 'table-cell' } }} align="right">Cost/Unit</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Supplier</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : filteredInventory.length > 0 ? (
                filteredInventory.map((item) => {
                  const status = getStockStatus(item);
                  const percentage = getStockPercentage(item);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                          Unit: {item.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={item.category} 
                          variant="outlined" 
                          sx={{ textTransform: 'capitalize', fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: 22 }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            {formatNumber(item.current_stock)} {item.unit}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              width: { xs: 40, sm: 60 },
                              height: 6,
                              borderRadius: 3,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: percentage < 25 ? '#f44336' : percentage < 50 ? '#ff9800' : '#4caf50',
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                          Min: {formatNumber(item.min_stock)} | Reorder: {formatNumber(item.reorder_point)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={status.icon}
                          label={status.label}
                          sx={{ 
                            bgcolor: status.bg, 
                            color: status.color, 
                            fontWeight: 600, 
                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                          {formatPrice(item.cost_per_unit)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {item.supplier || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Stock In">
                            <IconButton 
                              size="small" 
                              color="success" 
                              onClick={() => openStockDialog(item, 'in')}
                              sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                            >
                              <InIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stock Out">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => openStockDialog(item, 'out')}
                              sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                            >
                              <OutIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              onClick={() => openEditItem(item)}
                              sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                      No items found
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                      کوئی آئٹم نہیں ملا
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAddItem}
                      sx={{ mt: 2, bgcolor: '#ff6b35' }}
                    >
                      Add First Item
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ==================== RENDER RECIPES TAB ====================
  const renderRecipesTab = () => (
    <Box>
      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 1.5 
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Recipe Management / ریسپی مینجمنٹ
        </Typography>
        <Button
          variant="contained"
          startIcon={<RecipeIcon />}
          onClick={openRecipeDialog}
          fullWidth={isMobile}
          sx={{ 
            bgcolor: '#ff6b35', 
            '&:hover': { bgcolor: '#e55a2b' }, 
            borderRadius: 2,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          Add Recipe
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {menuItems.filter((m) => recipes.some((r) => r.menu_item_id === m.id)).length > 0 ? (
            menuItems.filter((m) => recipes.some((r) => r.menu_item_id === m.id)).map((item) => {
              const itemRecipes = recipes.filter((r) => r.menu_item_id === item.id);
              return (
                <Grid item xs={12} md={6} key={item.id}>
                  <Fade in timeout={300}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      transition: 'all 0.2s',
                      '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
                    }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                              {item.name_urdu}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={`${itemRecipes.length} ingredients`} 
                            sx={{ 
                              bgcolor: '#e3f2fd', 
                              color: '#1976d2', 
                              fontWeight: 600,
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: 24,
                            }} 
                          />
                        </Box>
                        <List dense sx={{ py: 0 }}>
                          {itemRecipes.map((rec, idx) => (
                            <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                                      {rec.ingredient_name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                                      {formatNumber(rec.quantity_needed)} {rec.unit}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Total Ingredients: {itemRecipes.length}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <RecipeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No recipes found
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 2 }}>
                  کوئی ریسپی نہیں ملی
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RecipeIcon />}
                  onClick={openRecipeDialog}
                  sx={{ bgcolor: '#ff6b35' }}
                >
                  Add First Recipe
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );

  // ==================== STAT CARDS ====================
  const StatCard = ({ title, value, icon, color, bg }) => (
    <Grid item xs={6} md={3}>
      <Fade in timeout={300}>
        <Card sx={{ 
          borderRadius: 3, 
          borderLeft: `4px solid ${color}`, 
          bgcolor: bg,
          transition: 'all 0.2s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1.5, sm: 2 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Avatar sx={{ bgcolor: color, color: '#fff', width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }}>
              {icon}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: color, 
                fontSize: { xs: '0.9rem', sm: '1.1rem' } 
              }}>
                {value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {title}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ 
      px: { xs: 1, sm: 2, md: 3 },
      pb: { xs: 8, sm: 3 },
    }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ===== HEADER ===== */}
      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexWrap: 'wrap', 
        gap: 1.5 
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#1a1a2e', 
            mb: 0.5,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
          }}>
            Inventory
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            انوینٹری | Stock & Recipe Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={loadData} 
            sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* ===== STATS ===== */}
      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <StatCard 
          title="Total Items" 
          value={inventory.length} 
          icon={<InventoryIcon />} 
          color="#1976d2" 
          bg="#e3f2fd" 
        />
        <StatCard 
          title="Low Stock" 
          value={lowStockItems.length} 
          icon={<WarningIcon />} 
          color="#ff9800" 
          bg="#fff3e0" 
        />
        <StatCard 
          title="Recipes" 
          value={[...new Set(recipes.map((r) => r.menu_item_id))].length} 
          icon={<RecipeIcon />} 
          color="#ed6c02" 
          bg="#fff3e0" 
        />
        <StatCard 
          title="Stock Value" 
          value={formatPrice(totalStockValue)} 
          icon={<MoneyIcon />} 
          color="#2e7d32" 
          bg="#e8f5e9" 
        />
      </Grid>

      {/* ===== TABS ===== */}
      <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'auto' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)} 
          sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
        >
          <Tab 
            icon={<InventoryIcon />} 
            label={isMobile ? '' : 'Stock / اسٹاک'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
          <Tab 
            icon={<RecipeIcon />} 
            label={isMobile ? '' : 'Recipes / ریسپیز'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
        </Tabs>
      </Paper>

      {/* ===== TAB CONTENT ===== */}
      {activeTab === 0 && renderInventoryTab()}
      {activeTab === 1 && renderRecipesTab()}

      {/* ===== ADD/EDIT ITEM DIALOG ===== */}
      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}>
          {itemDialogMode === 'add' ? 'Add Inventory Item' : 'Edit Item'} 
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
            {itemDialogMode === 'add' ? 'نیا سامان' : 'ترمیم'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Item Name" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} autoFocus />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="Category" value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}>
                {CATEGORIES.map((cat) => <MenuItem key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>{cat}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="Unit" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}>
                {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Current Stock" value={itemForm.current_stock} onChange={(e) => setItemForm({ ...itemForm, current_stock: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Cost Per Unit" value={itemForm.cost_per_unit} onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: parseFloat(e.target.value) || 0 })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" type="number" label="Min Stock" value={itemForm.min_stock} onChange={(e) => setItemForm({ ...itemForm, min_stock: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" type="number" label="Max Stock" value={itemForm.max_stock} onChange={(e) => setItemForm({ ...itemForm, max_stock: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" type="number" label="Reorder Point" value={itemForm.reorder_point} onChange={(e) => setItemForm({ ...itemForm, reorder_point: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Supplier" value={itemForm.supplier} onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })} placeholder="e.g., Metro, Makro, Local Market" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setItemDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={saveItem} 
            variant="contained" 
            startIcon={<SaveIcon />} 
            disabled={!itemForm.name.trim()} 
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== STOCK IN/OUT DIALOG ===== */}
      <Dialog open={stockDialog} onClose={() => setStockDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: stockAction === 'in' ? '#2e7d32' : '#d32f2f',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}>
          Stock {stockAction === 'in' ? 'In' : 'Out'} 
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
            {stockAction === 'in' ? 'اسٹاک آنا' : 'اسٹاک جانا'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
            <strong>{stockItem?.name}</strong> (Current: {formatNumber(stockItem?.current_stock)} {stockItem?.unit})
          </Typography>
          <TextField
            fullWidth
            type="number"
            size="small"
            label={`Quantity ${stockAction === 'in' ? 'Received' : 'Used'}`}
            value={stockQuantity}
            onChange={(e) => setStockQuantity(e.target.value)}
            autoFocus
            InputProps={{ endAdornment: <InputAdornment position="end">{stockItem?.unit}</InputAdornment> }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Note / نوٹ"
            value={stockNote}
            onChange={(e) => setStockNote(e.target.value)}
            placeholder="e.g., Purchased from Metro, Wastage due to expiry..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setStockDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleStockUpdate} 
            variant="contained" 
            color={stockAction === 'in' ? 'success' : 'error'} 
            startIcon={<SaveIcon />}
            sx={{ borderRadius: 2 }}
          >
            {stockAction === 'in' ? 'Stock In' : 'Stock Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== RECIPE DIALOG ===== */}
      <Dialog open={recipeDialog} onClose={() => setRecipeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}>
          Add Recipe / ریسپی شامل کریں
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Menu Item / ڈش</InputLabel>
            <Select 
              size="small"
              value={selectedMenuItem} 
              label="Menu Item" 
              onChange={(e) => setSelectedMenuItem(e.target.value)}
            >
              {menuItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name} ({item.name_urdu})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
            Ingredients / اجزاء
          </Typography>
          {recipeIngredients.map((ing, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1.5, alignItems: 'center' }}>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Ingredient"
                  value={ing.inventory_item_id}
                  onChange={(e) => updateRecipeIngredient(idx, 'inventory_item_id', e.target.value)}
                >
                  {inventory.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{item.name} ({item.unit})</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Quantity"
                  value={ing.quantity_needed}
                  onChange={(e) => updateRecipeIngredient(idx, 'quantity_needed', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Unit"
                  value={ing.unit}
                  onChange={(e) => updateRecipeIngredient(idx, 'unit', e.target.value)}
                >
                  {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={1}>
                <IconButton size="small" color="error" onClick={() => removeRecipeIngredient(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />} 
            onClick={addRecipeIngredient} 
            size="small"
            sx={{ borderRadius: 2 }}
          >
            Add Ingredient
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setRecipeDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={saveRecipe} 
            variant="contained" 
            startIcon={<SaveIcon />} 
            disabled={!selectedMenuItem || recipeIngredients.length === 0} 
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
          >
            Save Recipe
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DELETE CONFIRM ===== */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ ...deleteConfirm, open: false })} maxWidth="xs">
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#d32f2f',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}>
          <DeleteIcon /> Delete {deleteConfirm.name}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm({ ...deleteConfirm, open: false })} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;